import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const STATS = [
  { v: "100%", l: "Owner-led delivery" },
  { v: "DCMA-14", l: "Quality benchmark" },
  { v: "5+", l: "Industries served" },
  { v: "24/7", l: "Client response" },
];

const POINTS = [
  "Every project led by senior schedulers — never outsourced.",
  "Industry-standard tools (Primavera P6, MS Project) with custom dashboards.",
  "Executive-grade reporting tailored to your stakeholders.",
  "Owner, GC, sub & engineering perspectives — all under one roof.",
];

export const About = () => {
  return (
    <section
      data-testid="about-section"
      id="about"
      className="relative py-24 md:py-32 bg-[#050B1A] noise-overlay overflow-hidden"
    >
      {/* Construction blueprint backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1800&q=70"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08] mix-blend-luminosity"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B1A] via-[#050B1A]/85 to-[#050B1A]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#FFB300]" />
              <span className="font-mono-accent text-[11px] text-[#FFB300]">
                / About BlueLine
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F1F5F9] leading-[1.05] mb-8">
              Built for projects where{" "}
              <span className="text-[#00E5FF]">the schedule</span> is the
              project.
            </h2>
            <p className="text-[#94A3B8] text-base lg:text-lg leading-relaxed mb-6 max-w-2xl">
              BlueLine Control Systems LLC is a professional project scheduling
              and project controls consulting firm. We help construction,
              infrastructure, and engineering teams turn ambiguity into clear,
              defensible plans — and keep them on course from baseline through
              close-out.
            </p>
            <p className="text-[#94A3B8] text-base leading-relaxed mb-10 max-w-2xl">
              Every engagement is grounded in proven methodologies, industry
              tools, and direct partnership with owners, general contractors,
              subcontractors, and project stakeholders. The result: accurate,
              reliable, and actionable information that fuels confident
              decisions.
            </p>

            <ul className="space-y-3 max-w-xl">
              {POINTS.map((p, i) => (
                <motion.li
                  key={i}
                  data-testid={`about-point-${i}`}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex items-start gap-3 text-[#F1F5F9]"
                >
                  <CheckCircle2
                    size={18}
                    className="text-[#00E5FF] mt-0.5 flex-shrink-0"
                    strokeWidth={1.5}
                  />
                  <span className="text-sm lg:text-base">{p}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Right column — stats */}
          <div className="lg:col-span-5">
            <div className="border border-[#1E2A3B] glass-panel p-8 lg:p-10">
              <div className="font-mono-accent text-[10px] text-[#4A6E8C] mb-8">
                / Control Systems Snapshot
              </div>
              <div className="grid grid-cols-2 gap-px bg-[#1E2A3B]">
                {STATS.map((s, i) => (
                  <motion.div
                    key={i}
                    data-testid={`stat-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className="bg-[#0A101D] p-6"
                  >
                    <div className="font-heading text-3xl text-[#FFB300] mb-2">
                      {s.v}
                    </div>
                    <div className="font-mono-accent text-[10px] text-[#94A3B8]">
                      {s.l}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="divider-tech my-8" />

              <div className="font-mono-accent text-[10px] text-[#4A6E8C] mb-4">
                / Industries Served
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "Construction Mgmt",
                  "Infrastructure",
                  "Engineering",
                  "Program Mgmt",
                  "Consulting",
                ].map((t) => (
                  <span
                    key={t}
                    className="px-3 py-1.5 border border-[#1E2A3B] text-[11px] text-[#94A3B8] font-mono-accent"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
